import { map } from 'rxjs/operators';
import { forkJoin, Observable, Subject } from 'rxjs';
import { switchMap } from 'rxjs/internal/operators/switchMap';

import * as moment from 'moment';

import {
    ApiError,
    ServerResponse,
    HttpStatusCodesEnum,
    DocumentStatusesEnum,
    DocumentTypesEnum,
    IdentityDocumentTypesEnum,
    AuthApiService,
    IndividualDocumentInterface,
    LookupModel,
    IndividualDocumentModel,
    IndividualExtendedModel,
    IndividualDocumentInputModel,
    ApiResponse,
    CreateIndividualDocumentAndFilesRequestModel,
    CreateIndividualDocumentAndFilesInterface,
    IndividualDocumentsAtomicService,
    UpdateIndividualDocumentAndFilesInterface,
    UpdateIndividualDocumentAndFilesModel,
} from '@spa/api';

import { InArrayValidatorKey, RequiredValidatorKey } from '@spa/validators';

import { Helper, SelectControl } from '../../../../shared/classes';

import { ProofOfIdentityFormErrorStatesEnum } from '../../enums';

import { ProofOfIdentityControl } from '../../../../../assets/descriptors/panel-clients';

export class ProofOfIdentityForm {
    private authenticationService: AuthApiService;
    private identityDocumentTypes: LookupModel;
    private document: IndividualDocumentModel;
    private individualDocumentsAtomicService: IndividualDocumentsAtomicService;

    public individual: IndividualExtendedModel;
    public documentTypeControl: SelectControl;
    public documentsTuple: [Subject<string>, Subject<string>];
    public documentsTupleErrors: [Subject<string[]>, Subject<string[]>];
    public documentsTupleFiles: [File, File];
    public hiddenDocument: boolean;
    public reviewSummary: string;

    constructor(
        individualDocumentsAtomicService: IndividualDocumentsAtomicService,
        authenticationService: AuthApiService,
        identityDocumentTypes: LookupModel
    ) {
        this.documentsTuple = [new Subject(), new Subject()];
        this.documentsTupleErrors = [new Subject(), new Subject()];
        this.documentsTupleFiles = [null, null];
        this.individualDocumentsAtomicService = individualDocumentsAtomicService;
        this.authenticationService = authenticationService;
        this.identityDocumentTypes = identityDocumentTypes;
        this.hiddenDocument = false;
        this.reviewSummary = '';
    }

    private validateControl(messageFunction: (controlLabel?: string) => { [key: string]: string; }): boolean {
        const formsControlValue = this.documentTypeControl.value as string;

        if (!formsControlValue) {
            this.documentTypeControl.setError(messageFunction(this.documentTypeControl.placeholder)[RequiredValidatorKey]);

            return false;
        }

        const documentTypeIds = this.identityDocumentTypes.getTypeKeys();

        if (documentTypeIds.indexOf(formsControlValue) < 0) {
            this.documentTypeControl.setError(messageFunction(this.documentTypeControl.placeholder)[InArrayValidatorKey]);

            return false;
        }

        return true;
    }

    private createRegistrationAndUploadRequest(files: File[], identityDetailId: number):
        Observable<ApiResponse<ApiError[] | IndividualDocumentModel>> {
        const model = this.getIndividualDocumentAndFilesRequestModel(files, identityDetailId);

        return this.individualDocumentsAtomicService.AtomicCreateFile(model).pipe(
            map((response: ApiResponse<ApiError[] | IndividualDocumentInterface>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponse<IndividualDocumentInterface>(
                        response,
                        ProofOfIdentityFormErrorStatesEnum.UploadFileFailed
                    );
                }
                const data = response.data as IndividualDocumentInterface;
                const responseModel = new IndividualDocumentModel(data);

                if (!this.individual.documents) {
                    this.individual.documents = [];
                }

                this.individual.documents.push(responseModel);
                this.initDocumentsTuple(this.individual.documents);

                return Helper.createSuccessResponse<IndividualDocumentModel>(this.document);
            }));
    }

    private getIndividualDocumentAndFilesRequestModel(files: File[], identityDetailId: number):
        CreateIndividualDocumentAndFilesRequestModel {
        const documentType = !this.documentTypeControl ? IdentityDocumentTypesEnum.Passport : this.documentTypeControl.value;
        const ownDocumentModelInterface: CreateIndividualDocumentAndFilesInterface = {
            file_name: '',
            review_summary: this.reviewSummary,
            uploaded_by: this.authenticationService.getAuthenticatedUser().id,
            poi_document_id: identityDetailId,
            is_hidden_for_client: this.hiddenDocument,
            document_type: DocumentTypesEnum.ProofOfIdentity,
            sub_document_type: documentType as string,
            files,
            upload_stamp: new Date().toUTCString(),
            individual_id: this.individual.id,
        };

        return new CreateIndividualDocumentAndFilesRequestModel(ownDocumentModelInterface);
    }

    private updateIndividualDocumentRequest(): Observable<ApiResponse<ApiError[] | IndividualDocumentModel>> {
        const model = this.getIndividualDocumentAndFilesModelForUpdate();

        return this.individualDocumentsAtomicService.atomicUpdateFile(model).pipe(
            map((response: ApiResponse<ApiError[] | IndividualDocumentInterface>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponse<IndividualDocumentInterface>(
                        response,
                        ProofOfIdentityFormErrorStatesEnum.UpdateDocumentFailed
                    );
                }

                this.individual.documents.splice(this.individual.documents.findIndex(item => item.fileName === this.document.fileName
                    && item.documentType === this.document.documentType), 1);

                const data = response.data as IndividualDocumentInterface;
                const responseModel = new IndividualDocumentModel(data);
                this.document = responseModel;
                this.individual.documents.push(this.document);

                if (this.document.fileStorageIds.length > 1) {
                    this.documentsTuple[1].next(null);
                }

                responseModel.fileStorageIds.map((tuple: string, index) => {
                    this.documentsTuple[index].next(tuple);
                });

                return Helper.createSuccessResponse<IndividualDocumentModel>(this.document);
            }));
    }

    private getIndividualDocumentAndFilesModelForUpdate(): UpdateIndividualDocumentAndFilesModel {

        const files = this.documentsTupleFiles.filter(f => f !== null);
        files.forEach((fileOrBlob: any, i) => {
            if (!(fileOrBlob instanceof File)) {
                files[i] = new File([fileOrBlob], fileOrBlob.name);
            }

        });

        const documentUploadModelInterface: UpdateIndividualDocumentAndFilesInterface = {
            file_name: this.document.fileName,
            review_summary: this.document.reviewSummary,
            is_hidden_for_client: this.document.isHiddenForClient,
            document_type: DocumentTypesEnum.ProofOfIdentity,
            sub_document_type: this.documentTypeControl.value as string,
            files,
            uploaded_by: this.document.uploadedBy,
            upload_stamp: this.document.uploadStamp,
            id: this.document.id,
            modified_by: this.authenticationService.getAuthenticatedUser().id,
            modify_stamp: new Date().toUTCString(),
            individual_id: this.individual.id,
            file_storage_ids: this.document.fileStorageIds,
        };

        return new UpdateIndividualDocumentAndFilesModel(documentUploadModelInterface);
    }

    public getRequestModel(): [[File, number][], IndividualDocumentInputModel] {
        const filesForUpload = this.documentsTupleFiles
            .map((file: File, index: number) => [file, index])
            .filter((fileTuple: [File, number]) => fileTuple[0]) as [File, number][];
        filesForUpload.sort((a: [File, number], b: [File, number]) => {
            return a < b ? -1 : 1;
        });
        const documentType = !this.documentTypeControl ? IdentityDocumentTypesEnum.Passport : this.documentTypeControl.value;

        const model = new IndividualDocumentInputModel(
            DocumentTypesEnum.ProofOfIdentity,
            null,
            null,
            this.hiddenDocument,
            null,
            documentType as string,
            null,
            this.individual.id,
            this.reviewSummary
        );

        return [filesForUpload, model];
    }

    initFormControl(value = null): void {
        this.documentTypeControl = ProofOfIdentityControl();

        this.documentTypeControl.value = value;

        (this.documentTypeControl as SelectControl).options = this.identityDocumentTypes.getTypeKeys().map((key: string) => {
            return {
                label: this.identityDocumentTypes.getTypeValue(key),
                value: key
            };
        });
    }

    initDocumentsTuple(documents: IndividualDocumentModel[] = []): void {
        if (documents && documents.length > 0) {
            let filteredDocuments = [];

            const statuses = [
                DocumentStatusesEnum.New,
                DocumentStatusesEnum.InReview,
                DocumentStatusesEnum.Approved
            ] as number[];

            filteredDocuments = documents
                .filter((document: IndividualDocumentModel) => {
                    return (
                        (this.identityDocumentTypes.getTypeKeys().indexOf(document.subDocumentType) >= 0) &&
                        (statuses.indexOf(document.status) >= 0)
                    );
                })
                .sort((current: IndividualDocumentModel, next: IndividualDocumentModel) => {
                    return moment(next.uploadStamp).unix() - moment(current.uploadStamp).unix();
                });

            if (filteredDocuments.length !== 0) {
                this.document = filteredDocuments[0];

                this.documentTypeControl.value = this.document.subDocumentType;

                setTimeout(() => {
                    switch (filteredDocuments[0].fileStorageIds.length) {
                        case 1:
                            this.documentsTuple[0].next(filteredDocuments[0].fileStorageIds[0]);
                            this.documentsTuple[1].next(null);
                            break;
                        case 2:
                            this.documentsTuple[0].next(filteredDocuments[0].fileStorageIds[0]);
                            this.documentsTuple[1].next(filteredDocuments[0].fileStorageIds[1]);
                            break;
                        default:
                            this.documentsTuple[0].next(null);
                            this.documentsTuple[1].next(null);
                    }
                }, 10);
            }
        }
    }

    reInitDocumentsTuple(): void {
        this.document = null;

        this.documentsTuple[0].next(null);
        this.documentsTuple[1].next(null);

        this.resetDocumentsTuple();
    }

    validateDocumentsTuple(messageFunction: (controlLabel?: string) => { [key: string]: string; }): boolean {
        const documentType = !this.documentTypeControl ? IdentityDocumentTypesEnum.Passport : this.documentTypeControl.value;
        const tupleItemOneErrors = Helper.validateFile(this.documentsTupleFiles[0], messageFunction);

        let tupleItemTwoErrors = [];

        if (documentType !== IdentityDocumentTypesEnum.Passport) {
            tupleItemTwoErrors = Helper.validateFile(this.documentsTupleFiles[1], messageFunction);
        }

        this.documentsTupleErrors[0].next(tupleItemOneErrors);
        this.documentsTupleErrors[1].next(tupleItemTwoErrors);

        return (tupleItemOneErrors.length === 0) && (tupleItemTwoErrors.length === 0);
    }

    validateForm(messageFunction: (controlLabel?: string) => { [key: string]: string; }): boolean {
        return [
            this.validateDocumentsTuple(messageFunction),
            this.validateControl(messageFunction),
        ].indexOf(false) < 0;
    }

    onDocumentUpload(file: File, index: number): void {
        this.documentsTupleFiles[index] = file;
    }

    resetDocumentsTuple(): void {
        this.documentsTupleFiles[0] = null;
        this.documentsTupleFiles[1] = null;

        this.documentsTupleErrors[0].next([]);
        this.documentsTupleErrors[1].next([]);
    }

    resetDocumentsTuplesItem(index: number): void {
        this.documentsTupleFiles[index] = null;
        this.documentsTupleErrors[index].next([]);
    }

    createRequest(refreshDocument = true, identityDetailId: number = null): ServerResponse<IndividualDocumentModel> | null {
        const filesForUpload = this.documentsTupleFiles
            .map((file: File, index: number) => [file, index])
            .filter((fileTuple: [File, number]) => fileTuple[0]) as [File, number][];

        if (!this.document) {
            const filesArray = filesForUpload.map(x => x[0]);

            return this.createRegistrationAndUploadRequest(filesArray, identityDetailId);
        }

        const newFilesForUpload = filesForUpload.filter((fileTuple: [File, number]) => {
            return this.document.fileStorageIds.indexOf(fileTuple[0].name) < 0;
        });

        if (newFilesForUpload.length !== 0) {
            return this.updateIndividualDocumentRequest();
        }

        return null;
    }
}
